import type { HTMLAttributes, KeyboardEventHandler, ReactNode } from 'react';
import { useId, useState } from 'react';

export type InputFieldType =
    | 'text'
    | 'number'
    | 'email'
    | 'password'
    | 'search'
    | 'tel'
    | 'url';

export interface InputFieldProps {
    value: string;
    onChange: (value: string) => void;
    type?: InputFieldType;
    /** Shown when the field is empty and not focused. Cleared on focus. */
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    hasError?: boolean;
    className?: string;
    inputId?: string;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
    autoComplete?: string;
    name?: string;
    autoFocus?: boolean;
    /** Trailing unit or hint, shown beside the control (e.g. unit of measure). */
    suffix?: ReactNode;
    suffixId?: string;
    onBlur?: () => void;
    onFocus?: () => void;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

/**
 * Labeled text-style input. Hides the placeholder while focused so the hint
 * does not compete with typing.
 */
export function InputField({
    value,
    onChange,
    type = 'text',
    placeholder,
    label,
    disabled = false,
    hasError = false,
    className,
    inputId,
    ariaLabel,
    ariaDescribedBy,
    min,
    max,
    step,
    inputMode,
    autoComplete,
    name,
    autoFocus = false,
    suffix,
    suffixId,
    onBlur,
    onFocus,
    onKeyDown,
}: InputFieldProps) {
    const generatedId = useId();
    const resolvedInputId = inputId || generatedId;
    const [isFocused, setIsFocused] = useState(false);

    const rootClassName = ['input-field', className].filter(Boolean).join(' ');
    const controlClassName = ['input-field-control', hasError ? 'has-error' : '']
        .filter(Boolean)
        .join(' ');
    const shownPlaceholder = isFocused ? undefined : placeholder;

    const control = (
        <input
            id={resolvedInputId}
            className={controlClassName}
            type={type}
            name={name}
            value={value}
            placeholder={shownPlaceholder}
            disabled={disabled}
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={hasError || undefined}
            min={min}
            max={max}
            step={step}
            inputMode={inputMode}
            autoComplete={autoComplete}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => {
                setIsFocused(true);
                onFocus?.();
            }}
            onBlur={() => {
                setIsFocused(false);
                onBlur?.();
            }}
        />
    );

    return (
        <div className={rootClassName}>
            {label ? (
                <label className="input-field-label" htmlFor={resolvedInputId}>
                    {label}
                </label>
            ) : null}
            {suffix ? (
                <div className="input-field-control-row">
                    {control}
                    <span id={suffixId} className="input-field-suffix">
                        {suffix}
                    </span>
                </div>
            ) : (
                control
            )}
        </div>
    );
}

export default InputField;
